import { useTranslation } from 'react-i18next';
import { PHYSICAL_RADAR_TEMPLATE_CONFIG_IDS, RADAR_TEMPLATE_ORDER } from '@/consts/radar';
import { SYSTEM_WIDE_USER_ID } from '@/consts/userConfigs';
import { usePhysicalMetricsFeature } from '@/hooks/featureFlags';
import { UserConfigRadarTemplate } from '@/types/userConfigs';
import { sortObjectArray } from '@/utils/array';
import { convertToDropdownOption } from '@/utils/object';
import { getRadarTemplateDisplayName } from '@/utils/radar';

export const useRadarTemplateOptions = (radarTemplateConfigs: UserConfigRadarTemplate[]) => {
  const { t, i18n } = useTranslation('radar');
  const showPhysical = usePhysicalMetricsFeature();

  // Your Custom Profiles — owned by the current user, ordered alphabetically
  const ownTemplates = radarTemplateConfigs.filter(template => template.permission === 'own');
  const ownTemplateOptions = ownTemplates.map(template =>
    convertToDropdownOption(getRadarTemplateDisplayName(template, t), template.configId),
  );
  const sortedOwnOptions = sortObjectArray('label', ownTemplateOptions, i18n.language);

  // Shared with You — non-StatsBomb profiles shared by others, ordered alphabetically
  const sharedTemplates = radarTemplateConfigs.filter(
    template => template.hudlUserId !== SYSTEM_WIDE_USER_ID && template.permission !== 'own',
  );
  const sharedTemplateOptions = sharedTemplates.map(template =>
    convertToDropdownOption(getRadarTemplateDisplayName(template, t), template.configId),
  );
  const sortedSharedOptions = sortObjectArray('label', sharedTemplateOptions, i18n.language);

  // Statsbomb templates with predefined order
  const statsbombTemplates = radarTemplateConfigs.filter(
    template =>
      template.hudlUserId === SYSTEM_WIDE_USER_ID &&
      (showPhysical || !PHYSICAL_RADAR_TEMPLATE_CONFIG_IDS.has(template.configId)),
  );
  const statsbombTemplateOptions = statsbombTemplates.map(template => {
    // configId is a system-wide template id (filtered above), so it indexes RADAR_TEMPLATE_ORDER
    // directly; an id without an order entry yields undefined, matching the previous find() lookup.
    const order: number | undefined = RADAR_TEMPLATE_ORDER[template.configId as keyof typeof RADAR_TEMPLATE_ORDER];
    return convertToDropdownOption(getRadarTemplateDisplayName(template, t), template.configId, { order });
  });
  const sortedStatsbombOptions = sortObjectArray('optional.order', statsbombTemplateOptions, i18n.language);

  const groupedOptions = [
    { label: t('yourCustomProfiles'), options: sortedOwnOptions },
    { label: t('sharedWithYou'), options: sortedSharedOptions },
    { label: t('statsbombProfiles'), options: sortedStatsbombOptions },
  ];

  return groupedOptions;
};
